import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader, { SectionHeader } from "@/components/common/PageHeader";
import CrisisBanner from "@/components/common/CrisisBanner";
import EmptyState, { LoadingCard } from "@/components/common/EmptyState";
import { Sparkles, ClipboardCheck, ListTodo, Trophy, ArrowRight, Compass } from "lucide-react";

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{label}</div>
      <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{value ?? "—"}</div>
      {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
    </div>
  );
}

function ActionCard({ to, icon: Icon, title, description, accent = "sage", testId }) {
  const bg = {
    sage: "bg-sage-500 text-cream-50",
    clay: "bg-clay-500 text-white",
    care: "bg-accents-care text-white",
  }[accent] || "bg-sage-500 text-cream-50";
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group rounded-3xl bg-white shadow-card p-6 hover:shadow-card-hover transition hover:-translate-y-0.5 flex items-start gap-4"
    >
      <div className={`h-11 w-11 rounded-2xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-ink-900 text-lg tracking-tight">{title}</div>
        <div className="text-sm text-ink-500 mt-0.5">{description}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-ink-500 group-hover:text-ink-900 mt-2" strokeWidth={1.75} />
    </Link>
  );
}

export default function ClientHome() {
  const t = useT();
  const { user } = useAuth();

  const insights = useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await api.get("/insights")).data,
    retry: false,
  });
  const plan = useQuery({
    queryKey: ["plan"],
    queryFn: async () => (await api.get("/plan")).data,
    retry: false,
  });
  const victories = useQuery({
    queryKey: ["victories"],
    queryFn: async () => (await api.get("/victories")).data,
    retry: false,
  });

  const name = user?.name?.split(" ")?.[0] || user?.display_name || "";

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("brand.name")}
        title={name ? `${t("client.greeting")} ${name && ""}` : t("client.greeting")}
        subtitle={t("brand.tagline")}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard
          testId="home-action-companion"
          to="/app/companion"
          icon={Sparkles}
          title={t("client.startCompanion")}
          description={t("nav.companion")}
        />
        <ActionCard
          testId="home-action-checkin"
          to="/app/checkins"
          icon={ClipboardCheck}
          title={t("client.quickCheckin")}
          description={t("nav.checkins")}
          accent="clay"
        />
        <ActionCard
          testId="home-action-plan"
          to="/app/plan"
          icon={ListTodo}
          title={t("client.yourPlan")}
          description={t("nav.plan")}
          accent="care"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard testId="home-stat-plan" label={t("nav.plan")} value={Array.isArray(plan.data?.items) ? plan.data.items.length : (plan.data?.length ?? "—")} sub={plan.isLoading ? t("common.loading") : ""} />
        <StatCard testId="home-stat-victories" label={t("nav.victories")} value={Array.isArray(victories.data) ? victories.data.length : (victories.data?.count ?? "—")} sub={victories.isLoading ? t("common.loading") : ""} />
        <StatCard testId="home-stat-insight" label={t("client.weeklyInsight")} value={insights.data?.summary?.slice?.(0, 32) || (insights.data ? "✓" : "—")} sub={insights.isLoading ? t("common.loading") : ""} />
      </div>

      <CrisisBanner />

      <section>
        <SectionHeader title={t("client.recentVictories")} />
        {victories.isLoading && <LoadingCard height="h-24" />}
        {!victories.isLoading && (!victories.data || (Array.isArray(victories.data) && victories.data.length === 0)) && (
          <EmptyState
            icon={Trophy}
            title={t("common.empty")}
            description={t("client.victoryToday")}
            testId="home-victories-empty"
            action={
              <Link to="/app/victories" className="inline-flex rounded-full bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 text-sm font-medium">
                {t("client.victoryToday")}
              </Link>
            }
          />
        )}
        {Array.isArray(victories.data) && victories.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {victories.data.slice(0, 6).map((v, i) => (
              <div key={v.id || i} className="rounded-2xl bg-white shadow-card p-4" data-testid={`home-victory-${i}`}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-sage-600 mb-1">✦</div>
                <div className="text-ink-900 text-sm">{v.text || v.title || v.note || "Victory"}</div>
                {v.created_at && <div className="text-xs text-ink-500 mt-1">{new Date(v.created_at).toLocaleDateString()}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="rounded-3xl bg-white shadow-card p-8 flex items-center gap-6">
        <div className="h-14 w-14 rounded-2xl bg-sage-100 flex items-center justify-center shrink-0">
          <Compass className="h-6 w-6 text-sage-700" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl text-ink-900">{t("client.startCompanion")}</div>
          <div className="text-sm text-ink-500">{t("brand.tagline")}</div>
        </div>
        <Link
          to="/app/companion"
          data-testid="home-open-companion"
          className="rounded-full bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 text-sm font-medium"
        >
          {t("common.open")}
        </Link>
      </div>
    </div>
  );
}
